import { rwColors } from '@/styles/colors';
import { GroupBase, Props, StylesConfig } from 'react-select';
import { SelectField } from './SelectField';

const DEFAULT_MENU_PORTAL_Z_INDEX = 110;

export type FilterSelectSize = 'sm' | 'md' | 'lg';

export type FilterSelectStyleProps = {
  size?: FilterSelectSize;
  labelColor?: string;
  valuePaddingLeft?: number;
  menuMaxHeight?: number;
  menuPortalZIndex?: number;
  showDropdownIndicator?: boolean;
  showIndicatorSeparator?: boolean;
  themedMultiValue?: boolean;
};

type FilterSelectFieldProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<Props<Option, IsMulti, Group>, 'styles'> & {
  label?: React.ReactNode;
  styleProps?: FilterSelectStyleProps;
};

const sizePresetMap: Record<
  FilterSelectSize,
  {
    controlHeight?: number;
    fontSize?: number;
    singleValueFontSize?: number;
    optionPaddingTop?: number;
    optionPaddingBottom?: number;
    dropdownIndicatorPadding?: number;
    clearIndicatorPadding?: number;
  }
> = {
  sm: {
    controlHeight: 25,
    fontSize: 12,
    singleValueFontSize: 12,
    optionPaddingTop: 0,
    optionPaddingBottom: 0,
    dropdownIndicatorPadding: 2,
    clearIndicatorPadding: 2,
  },
  md: {
    fontSize: 14,
  },
  lg: {
    controlHeight: 40,
    fontSize: 12,
    singleValueFontSize: 12,
    optionPaddingTop: 6,
    optionPaddingBottom: 6,
    dropdownIndicatorPadding: 8,
    clearIndicatorPadding: 6,
  },
};

const buildFilterSelectStyles = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  styleProps: FilterSelectStyleProps = {}
): StylesConfig<Option, IsMulti, Group> => {
  const {
    size = 'md',
    labelColor,
    valuePaddingLeft,
    menuMaxHeight,
    menuPortalZIndex = DEFAULT_MENU_PORTAL_Z_INDEX,
    showDropdownIndicator = true,
    showIndicatorSeparator = false,
    themedMultiValue = false,
  } = styleProps;

  const sizePreset = sizePresetMap[size];

  return {
    control: (provided) => {
      return {
        ...provided,
        boxShadow: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        minHeight: sizePreset.controlHeight,
        height: sizePreset.controlHeight,
        '&:hover': { borderColor: labelColor ?? rwColors.main },
      };
    },
    dropdownIndicator: (provided) =>
      showDropdownIndicator
        ? {
            ...provided,
            ...(sizePreset.dropdownIndicatorPadding !== undefined
              ? { padding: sizePreset.dropdownIndicatorPadding }
              : {}),
          }
        : { display: 'none' },
    indicatorSeparator: () =>
      showIndicatorSeparator ? {} : { display: 'none' },
    valueContainer: (provided) => {
      return {
        ...provided,
        height: sizePreset.controlHeight,
        ...(valuePaddingLeft !== undefined
          ? { paddingLeft: `${valuePaddingLeft}px` }
          : {}),
      };
    },
    input: (provided) => ({
      ...provided,
      marginLeft: 0,
      ...(sizePreset.fontSize !== undefined
        ? { fontSize: `${sizePreset.fontSize}px` }
        : {}),
    }),
    placeholder: (provided) => ({
      ...provided,
      color: rwColors.gray,
      marginLeft: 0,
      ...(sizePreset.fontSize !== undefined
        ? { fontSize: `${sizePreset.fontSize}px` }
        : {}),
    }),
    singleValue: (provided) => ({
      ...provided,
      ...(sizePreset.singleValueFontSize !== undefined
        ? { fontSize: `${sizePreset.singleValueFontSize}px` }
        : sizePreset.fontSize !== undefined
          ? { fontSize: `${sizePreset.fontSize}px` }
          : {}),
    }),
    clearIndicator: (provided) => ({
      ...provided,
      ...(sizePreset.clearIndicatorPadding !== undefined
        ? { padding: sizePreset.clearIndicatorPadding }
        : {}),
    }),
    option: (provided, { isFocused }) => ({
      ...provided,
      color: isFocused ? 'white' : provided.color,
      backgroundColor: isFocused ? rwColors.main : provided.backgroundColor,
      ...(sizePreset.fontSize !== undefined
        ? { fontSize: `${sizePreset.fontSize}px` }
        : {}),
      ...(sizePreset.optionPaddingTop !== undefined
        ? { paddingTop: sizePreset.optionPaddingTop }
        : {}),
      ...(sizePreset.optionPaddingBottom !== undefined
        ? { paddingBottom: sizePreset.optionPaddingBottom }
        : {}),
    }),
    multiValue: (provided) =>
      themedMultiValue
        ? {
            ...provided,
            backgroundColor: rwColors.main,
            color: 'white',
          }
        : provided,
    multiValueLabel: (provided) =>
      themedMultiValue ? { ...provided, color: 'white' } : provided,
    multiValueRemove: (provided) =>
      themedMultiValue
        ? {
            ...provided,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': { backgroundColor: 'transparent' },
            '> svg': {
              width: '10px',
              height: '10px',
              backgroundColor: 'white',
              borderRadius: '50%',
              color: rwColors.main,
            },
          }
        : provided,
    menu: (provided) => ({
      ...provided,
      ...(menuMaxHeight ? { maxHeight: menuMaxHeight } : {}),
      ...(sizePreset.fontSize !== undefined
        ? { fontSize: `${sizePreset.fontSize}px` }
        : {}),
    }),
    menuList: (provided) => ({
      ...provided,
      ...(menuMaxHeight ? { maxHeight: menuMaxHeight } : {}),
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: menuPortalZIndex,
    }),
  };
};

export const FilterSelectField = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  label,
  styleProps,
  menuShouldScrollIntoView = false,
  menuPortalTarget,
  ...props
}: FilterSelectFieldProps<Option, IsMulti, Group>) => {
  return (
    <SelectField<Option, IsMulti, Group>
      label={label}
      menuShouldScrollIntoView={menuShouldScrollIntoView}
      menuPortalTarget={
        menuPortalTarget ??
        (typeof window !== 'undefined' ? document.body : undefined)
      }
      styles={buildFilterSelectStyles<Option, IsMulti, Group>(styleProps)}
      {...props}
    />
  );
};
